import { useState } from "react";
import { motion } from "framer-motion";

import { SPINE } from "../data/site";
import { PILLARS } from "../data/capabilities";

/**
 * The delivery spine — the one diagram the whole site is organised around.
 *
 * Think → Design → Build → Deploy → Operate, with the three ways to engage
 * us drawn as spans underneath rather than as three parallel cards. The
 * distinction is the entire strategic point: three cards say "we sell three
 * services", which is what every IT services company says. Three spans on
 * one line say "this is one capability, and you can enter it anywhere" —
 * and it makes visible the thing a body shop cannot claim, which is that
 * the Product span reaches all the way to Operate.
 *
 * Hovering a span dims the stages it does not cover. That interaction is
 * the explanation; without it the reader has to count columns.
 */
export function Spine({ compact = false }: { compact?: boolean }) {
  const [active, setActive] = useState<string | null>(null);

  const activePillar = PILLARS.find((p) => p.key === active) ?? null;
  const isDimmed = (stageKey: string) =>
    activePillar ? !activePillar.covers.includes(stageKey as never) : false;

  return (
    <div className="relative">
      {/* ── Desktop / tablet: horizontal ─────────────────────────────── */}
      <div className="hidden md:block">
        {/* Stage row */}
        <div className="grid grid-cols-5 gap-3">
          {SPINE.map((s, i) => (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.4, delay: i * 0.07 }}
              className={`transition-opacity duration-300 ${
                isDimmed(s.key) ? "opacity-25" : "opacity-100"
              }`}
            >
              <div className="font-display text-base font-semibold tracking-tight text-white">
                {s.label}
              </div>
              {!compact ? (
                <p className="mt-1.5 text-xs leading-relaxed text-white/50">{s.body}</p>
              ) : null}
            </motion.div>
          ))}
        </div>

        {/* The line itself, with a node per stage sitting on it. */}
        <div className="relative mt-5 h-px">
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: "left" }}
            className="absolute inset-0 bg-gradient-to-r from-emerald-400/70 via-teal-400/60 to-violet-500/70"
          />
          <div className="absolute inset-0 grid grid-cols-5">
            {SPINE.map((s, i) => (
              <div key={s.key} className="relative">
                <motion.span
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.3, delay: 0.35 + i * 0.09 }}
                  className={`absolute -top-[3px] left-0 size-[7px] rounded-full bg-white transition-opacity duration-300 ${
                    isDimmed(s.key) ? "opacity-25" : "opacity-100"
                  }`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Engagement spans */}
        <div className="mt-4 grid grid-cols-5 gap-y-2.5">
          {PILLARS.map((p, row) => {
            const first = SPINE.findIndex((s) => p.covers.includes(s.key));
            const last =
              SPINE.length -
              1 -
              [...SPINE].reverse().findIndex((s) => p.covers.includes(s.key));
            const dim = active !== null && active !== p.key;
            return (
              <motion.a
                key={p.key}
                href={p.href}
                onMouseEnter={() => setActive(p.key)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(p.key)}
                onBlur={() => setActive(null)}
                initial={{ opacity: 0, scaleX: 0.85 }}
                whileInView={{ opacity: 1, scaleX: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: 0.5 + row * 0.09 }}
                style={{
                  gridColumnStart: first + 1,
                  gridColumnEnd: last + 2,
                  transformOrigin: "left",
                }}
                className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 transition duration-300 ${
                  dim
                    ? "border-white/5 bg-white/[0.02] opacity-40"
                    : "border-white/10 bg-white/[0.04] hover:border-white/25"
                }`}
              >
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/80">
                  {p.short}
                </span>
                <span className="truncate text-xs text-white/45">{p.title}</span>
              </motion.a>
            );
          })}
        </div>
      </div>

      {/* ── Mobile: the same data, read top to bottom ────────────────── */}
      <div className="md:hidden">
        <ol className="relative space-y-5 border-l border-white/10 pl-5">
          {SPINE.map((s, i) => {
            const covering = PILLARS.filter((p) => p.covers.includes(s.key));
            return (
              <motion.li
                key={s.key}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                className="relative"
              >
                <span className="absolute -left-[23px] top-1.5 size-[7px] rounded-full bg-gradient-to-br from-emerald-400 to-violet-500" />
                <div className="font-display text-[15px] font-semibold text-white">
                  {s.label}
                </div>
                <p className="mt-1 text-[13px] leading-relaxed text-white/55">{s.body}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {covering.map((p) => (
                    <span
                      key={p.key}
                      className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white/55"
                    >
                      {p.short}
                    </span>
                  ))}
                </div>
              </motion.li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
